import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import toast from '../shared/Toast';
import { saveFile } from '../../_utils/fileExport';
import { hapticSuccess, hapticTick } from '../../_utils/haptics';

/**
 * A QR code for the booking link, to print for a window, till or flyer.
 * The thumbnail is drawn live from the link, so it follows a renamed link,
 * and Preview shows it large enough to scan from the screen with a phone.
 * The download is a print-ready PNG (the code, "Scan to book" and the
 * business name) handed over by saveFile: the share sheet in the app
 * (Save Image, AirDrop, Files), a normal download on the web.
 *
 * qrcode is loaded on first use, so it never weighs on the rest of settings.
 */
const INK = '#29282D';
const FONT = '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif';

const loadQr = () => import(/* webpackChunkName: "qrcode" */ 'qrcode').then((m) => m.default || m);

async function posterBlob(url, name) {
    const QR = await loadQr();
    const W = 1200, H = 1480, SIZE = 960, TOP = 120;
    const code = document.createElement('canvas');
    await QR.toCanvas(code, url, { width: SIZE, margin: 0, errorCorrectionLevel: 'M', color: { dark: INK, light: '#ffffff' } });
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);
    ctx.drawImage(code, (W - SIZE) / 2, TOP, SIZE, SIZE);
    ctx.textAlign = 'center';
    ctx.fillStyle = INK;
    ctx.font = `700 68px ${FONT}`;
    ctx.fillText('Scan to book', W / 2, TOP + SIZE + 130);
    if (name) {
        // Shrink a long name to fit, rather than cutting it off.
        let size = 42;
        ctx.font = `400 ${size}px ${FONT}`;
        while (size > 24 && ctx.measureText(name).width > W - 160) { size -= 2; ctx.font = `400 ${size}px ${FONT}`; }
        ctx.fillStyle = '#6b6a70';
        ctx.fillText(name, W / 2, TOP + SIZE + 200);
    }
    return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('No image'))), 'image/png'));
}

function fileName(url) {
    const slug = String(url || '').replace(/[?#].*$/, '').replace(/\/+$/, '').split('/').pop() || 'booking';
    return `booking-qr-${slug.replace(/[^a-z0-9-]+/gi, '-').toLowerCase()}.png`;
}

export default function BookingQr({ url, name }) {
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);

    // Drawn big enough for the full-size preview; the thumbnail scales it down.
    useEffect(() => {
        let alive = true;
        setPreview(null);
        if (!url) return undefined;
        loadQr()
            .then((QR) => QR.toDataURL(url, { width: 640, margin: 0, errorCorrectionLevel: 'M', color: { dark: INK, light: '#ffffff' } }))
            .then((data) => { if (alive) setPreview(data); })
            .catch(() => { if (alive) setPreview(null); });
        return () => { alive = false; };
    }, [url]);

    const show = () => { if (!preview) return; hapticTick(); setOpen(true); };

    // Nothing over the code while it's being scanned: Bailey's desk pet and
    // its cards sit above modals, so they step aside while this is open.
    useEffect(() => {
        if (!open) return undefined;
        document.body.classList.add('bu-qr-open');
        return () => document.body.classList.remove('bu-qr-open');
    }, [open]);

    const download = async () => {
        if (!url || busy) return;
        hapticTick();
        setBusy(true);
        try {
            const blob = await posterBlob(url, name);
            const how = await saveFile({ blob, filename: fileName(url), title: 'Booking QR code' });
            if (how === 'download') toast.success('QR code downloaded');
            if (how !== 'cancelled') hapticSuccess();
        } catch (e) {
            toast.error('Couldn’t make the QR code. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    if (!url) return null;
    return (
        <div className="bu-qr">
            <button type="button" className="bu-qr-code" onClick={show} disabled={!preview} aria-label="Preview QR code">
                {preview ? <img src={preview} alt="" /> : <span className="bu-qr-blank" />}
            </button>
            <div className="bu-qr-text">
                <span className="bu-qr-title">QR code</span>
                <span className="bu-hint">Print it for your window, till or flyers. Scanning it opens this link.</span>
            </div>
            <div className="bu-qr-actions">
                <button type="button" className="bu-qr-btn is-quiet" onClick={show} disabled={!preview}>Preview</button>
                <button type="button" className="bu-qr-btn" onClick={download} disabled={busy || !preview}>
                    {busy ? 'Preparing…' : 'Download'}
                </button>
            </div>

            <Modal show={open} onHide={() => setOpen(false)} centered dialogClassName="bu-qr-modal" aria-labelledby="bu-qr-modal-title">
                <div className="bu-qr-sheet">
                    <h3 id="bu-qr-modal-title">Scan to test</h3>
                    <p className="bu-hint">Point your phone’s camera at the code. It should open your booking page.</p>
                    {preview && <img className="bu-qr-large" src={preview} alt={`QR code for ${url}`} />}
                    <p className="bu-qr-url">{url}</p>
                    <div className="bu-qr-sheet-actions">
                        <button type="button" className="bu-qr-btn is-quiet" onClick={() => setOpen(false)}>Close</button>
                        <button type="button" className="bu-qr-btn" onClick={download} disabled={busy}>
                            {busy ? 'Preparing…' : 'Download'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
